import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Empty, List, Spin, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../../stores/root-store-context';
import { ErpEntityType, ErpHistoryEntry } from '../../../../api/FinanceApiClient';
import { describeAction, formatHistoryEntry } from './erpHistory';

const { Text } = Typography;

const ACTION_COLORS: Record<ErpHistoryEntry['action'], string> = {
  Created: 'green',
  Updated: 'blue',
  Deleted: 'red',
};

export interface ErpHistoryPanelProps {
  entityType: ErpEntityType;
  entityId: number;
  /**
   * Load only while the panel is on screen (drawer open / tab active). Fetching is keyed on this so
   * the versions load when the panel first shows and re-load when the entity changes underneath it.
   */
  active: boolean;
}

/**
 * The versioned change-log list (who/when + field-level before→after), newest first — the shared
 * body used both by {@link ErpHistoryDrawer} and by the Operation detail drawer's History tab.
 * Loading is local (never the store-wide `isLoading`), so showing history never dims or unmounts the
 * page/list behind it.
 */
const ErpHistoryPanel: React.FC<ErpHistoryPanelProps> = observer(({ entityType, entityId, active }) => {
  const { t } = useTranslation();
  const { financeStore } = useStore();
  const [entries, setEntries] = useState<ErpHistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!active || !entityId) return;
    let cancelled = false;
    setEntries([]);
    setLoading(true);
    financeStore
      .getErpHistory(entityType, entityId)
      .then(history => {
        if (!cancelled) setEntries(history);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, entityType, entityId]);

  const sorted = [...entries].sort((a, b) => b.version - a.version);

  return (
    <Spin spinning={loading}>
      <List
        dataSource={sorted}
        locale={{ emptyText: <Empty description={t('NoHistoryYet')} /> }}
        renderItem={entry => {
          const formatted = formatHistoryEntry(entry);
          return (
            <List.Item key={`${entry.version}-${entry.action}`}>
              <div style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text strong>{formatted.title}</Text>
                  <Tag color={ACTION_COLORS[entry.action]}>{t(describeAction(entry.action))}</Tag>
                </div>
                <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: 12, margin: '4px 0 8px' }}>
                  {t('HistoryBy', { who: formatted.who || t('Unknown') })} &middot; {formatted.when}
                </div>
                {formatted.lines.length === 0 ? (
                  <Text type="secondary">{t('HistoryNoChanges')}</Text>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', fontWeight: 500, paddingRight: 8 }}>{t('Field')}</th>
                        <th style={{ textAlign: 'left', fontWeight: 500, paddingRight: 8 }}>{t('From')}</th>
                        <th style={{ textAlign: 'left', fontWeight: 500 }}>{t('To')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {formatted.lines.map(line => (
                        <tr key={line.field}>
                          <td style={{ paddingRight: 8 }}>{t(line.field)}</td>
                          <td style={{ paddingRight: 8 }}>
                            <Text delete>{line.from ?? '—'}</Text>
                          </td>
                          <td>
                            <Text strong>{line.to ?? '—'}</Text>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </List.Item>
          );
        }}
      />
    </Spin>
  );
});

export default ErpHistoryPanel;
